import { useEffect, useState } from "react";
import type { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

/** Store/refresh the signed-in person's record so we can report sign-ups weekly. */
export async function recordProfile(user: User) {
  const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
  const fullName =
    typeof meta["full_name"] === "string"
      ? (meta["full_name"] as string)
      : typeof meta["name"] === "string"
        ? (meta["name"] as string)
        : null;

  // Only write full_name when auth metadata actually has one — otherwise the
  // upsert would overwrite a name the user saved on the Account page with NULL.
  const row: {
    id: string;
    email: string;
    last_sign_in_at: string;
    full_name?: string;
  } = {
    id: user.id,
    email: user.email ?? "",
    last_sign_in_at: new Date().toISOString(),
  };
  if (fullName) row.full_name = fullName;

  const { error } = await supabase.from("profiles").upsert(row, { onConflict: "id" });
  if (error) console.error("[profiles] upsert failed", error.message);
}

export function useSession() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setUser(data.session?.user ?? null);
      setLoading(false);
      if (data.session?.user) void recordProfile(data.session.user);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      setUser(session?.user ?? null);
      setLoading(false);
      if (event === "SIGNED_IN" && session?.user) void recordProfile(session.user);
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return { user, loading };
}
